import { Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { getProceduresByPetIdForCertificate } from "../../../redux/reducers/petSlice";
import { a4PrintConfig } from "../../../util/commonConfig";
import { downloadTypeList } from "../../../util/dropList";
import { genderEnum } from "../../../util/enum";
import { printFromNode, warmPrintFrame } from "../../../util/fastPrint";
import { getRightPackageUom } from "../../../util/function";
import { getPetAgeDisplay } from "../../../util/getAge";
import ContentToPrint from "../../CommonScreens/ContentToPrint/ContentToPrint";
import CertificatePreviewModal from "../../Hospital/Plan/CertificatePreviewModal";
import { STANDARD_CERTIFICATES } from "../../Hospital/Plan/certificateConstants";

const initValues = {
  type: "Rabies Certificate",
  range: "all",
  startDate: dayjs(),
  endDate: dayjs(),
};

const getLinkedServiceIdsFromCert = (cert) =>
  (cert?.linkedServices ?? [])
    .map((entry) =>
      typeof entry === "object" && entry != null
        ? (entry?.serviceId ?? entry?.id ?? entry?.value)
        : entry,
    )
    .filter((id) => id != null && id !== "")
    .map((id) => String(id));

const parseLocalUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const CerificatesDownloadModal = ({
  modVsble = false,
  onCloseMod = () => {},
  vaccinationOnly = false,
}) => {
  const dispatch = useDispatch();
  const contentToPrint = useRef(null);
  const certToPrint = useRef(null);
  const location = useLocation();
  const { appointment } = location.state;
  const [modValues, setModValues] = useState(initValues);
  const [prevModVsble, setPrevModVsble] = useState(false);
  const [certModVsble, setCertModVsble] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCert, setPreviewCert] = useState(null);
  const { certificateProcedure, petDetails } = useSelector(
    (state) => state?.pet,
  );
  const clinicFromRedux = useSelector((state) => state?.clinic?.details);
  const clinicCertificates = useSelector(
    (state) => state?.clinic?.certificates,
  );
  const appData = useSelector((state) => state?.pet?.complaintSummary?.data);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const apntId = petAndApntData?.appointment?.appointmentId;
  const petData = petAndApntData?.pet; // apntId ? appData?.pet : petDetails;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const profile = useMemo(() => parseLocalUser(), []);
  const {
    stocks,
    clinicFromMandate,
    serviceRemainders,
    mandateCertificates,
    mandateServices,
  } = useMemo(() => {
    if (!mandateDataRaw) {
      return {
        stocks: [],
        clinicFromMandate: {},
        serviceRemainders: [],
        mandateCertificates: [],
        mandateServices: [],
      };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicFromMandate: reqData?.clinic ?? {},
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        serviceRemainders: Array.isArray(reqData?.serviceRemainders)
          ? reqData.serviceRemainders
          : [],
        mandateCertificates: Array.isArray(reqData?.certificates)
          ? reqData.certificates
          : [],
        mandateServices: Array.isArray(reqData?.services)
          ? reqData.services
          : Array.isArray(reqData?.services?.data)
            ? reqData.services.data
            : [],
      };
    } catch {
      return {
        stocks: [],
        clinicFromMandate: {},
        serviceRemainders: [],
        mandateCertificates: [],
        mandateServices: [],
      };
    }
  }, [mandateDataRaw]);

  const certificatesList = useMemo(() => {
    if (Array.isArray(clinicCertificates) && clinicCertificates.length > 0) {
      return clinicCertificates;
    }
    return mandateCertificates;
  }, [clinicCertificates, mandateCertificates]);

  const clinicDet = useMemo(() => {
    const mandate = clinicFromMandate ?? {};
    const redux =
      clinicFromRedux && typeof clinicFromRedux === "object"
        ? clinicFromRedux
        : {};
    const merged = {
      ...redux,
      ...mandate,
      image: mandate?.image || redux?.image || null,
      footer: mandate?.footer || redux?.footer || null,
      name: mandate?.name || redux?.name || "",
      address: mandate?.address || redux?.address || "",
      taxId: mandate?.taxId || redux?.taxId || "",
      mobile: mandate?.mobile || redux?.mobile || "",
    };

    const profileId = profile?.id ?? profile?.userId ?? null;
    const clinicId = merged?.id ?? null;
    const idsMatch =
      profileId != null &&
      clinicId != null &&
      String(profileId) === String(clinicId);

    // Use logo only when localStorage user id matches clinic.id
    if (idsMatch) {
      merged.image = merged.image || profile?.image || null;
    } else {
      merged.image = null;
    }

    return merged;
  }, [clinicFromMandate, clinicFromRedux, profile]);

  const onChange = (name, value) => {
    setModValues((prev) => ({ ...prev, [name]: value }));
  };

  const toDayjsDate = (dateValue) => {
    if (dateValue == null || dateValue === "") return null;

    if (typeof dateValue === "string") {
      const datePart = dateValue.split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return dayjs(datePart).startOf("day");
      }
    }

    if (dayjs.isDayjs(dateValue)) {
      return dateValue.isValid() ? dateValue.startOf("day") : null;
    }

    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return dayjs(dateValue).startOf("day");
    }

    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed.startOf("day") : null;
  };

  const handlePreview = () => {
    // if (modValues?.type === "Vaccine certificate") {
    dispatch(
      getProceduresByPetIdForCertificate({
        petId: appointment?.appoinment?.petId,
        // url: defaultUrl,
      }),
    ).then((res) => {
      if (res?.payload) setPrevModVsble(true);
    });
    // }
  };

  const getStockCategoryById = (stckId = null) => {
    if (!stckId) return null;
    const reqStock = stocks?.find((stc) => stc?.stockId === stckId);
    return (reqStock?.category ?? "")?.toLowerCase();
  };

  const getSelectedCertificateType = () =>
    vaccinationOnly
      ? "Vaccination History"
      : `${modValues?.type ?? ""} History`;

  const getCertificateLookupName = () =>
    vaccinationOnly ? "Vaccination Certificate" : (modValues?.type ?? "");

  const showFullCertColumns =
    vaccinationOnly ||
    String(modValues?.type ?? "")
      .trim()
      .toLowerCase() === "rabies certificate";

  const getCertificateByLookupName = (typeName) => {
    if (!typeName) return null;
    const normalized = String(typeName).trim().toLowerCase();
    const template =
      STANDARD_CERTIFICATES.find(
        (cert) =>
          String(cert?.name ?? "")
            .trim()
            .toLowerCase() === normalized,
      ) ?? null;
    const clinicCert =
      (certificatesList ?? []).find(
        (cert) =>
          String(cert?.name ?? "")
            .trim()
            .toLowerCase() === normalized,
      ) ?? null;
    if (!template && !clinicCert) return null;
    return {
      ...template,
      ...clinicCert,
      id: template?.id ?? clinicCert?.id,
      name: clinicCert?.name ?? template?.name ?? typeName,
      note: clinicCert?.note ?? clinicCert?.footerNote ?? template?.note,
      footerNote:
        clinicCert?.note ?? clinicCert?.footerNote ?? template?.footerNote,
      linkedServices:
        clinicCert?.linkedServices ?? template?.linkedServices ?? [],
      rabiesSourceService:
        clinicCert?.rabiesVaccination ??
        clinicCert?.rabiesSourceService ??
        template?.rabiesSourceService ??
        null,
      parasiteSourceService:
        clinicCert?.parasiteTreatment ??
        clinicCert?.parasiteSourceService ??
        template?.parasiteSourceService ??
        null,
    };
  };

  const getSelectedCertificate = () =>
    getCertificateByLookupName(getCertificateLookupName());

  const getLinkedMatchersForCert = (cert) => {
    const linkedIds = getLinkedServiceIdsFromCert(cert);
    const resolvedIds = new Set();
    const linkedNames = new Set();

    linkedIds.forEach((idOrName) => {
      const asNum = Number(idOrName);
      if (!Number.isNaN(asNum) && asNum > 0) {
        resolvedIds.add(String(idOrName));
        return;
      }
      linkedNames.add(String(idOrName).trim().toLowerCase());
    });

    (cert?.linkedServices ?? []).forEach((entry) => {
      if (typeof entry === "object" && entry != null) {
        const name = entry?.serviceName ?? entry?.name;
        if (name) linkedNames.add(String(name).trim().toLowerCase());
      }
    });

    (mandateServices ?? []).forEach((ser) => {
      const serName = String(ser?.serviceName ?? ser?.name ?? "")
        .trim()
        .toLowerCase();
      const serId = ser?.serviceId;
      if (serId != null && serId !== "" && linkedNames.has(serName)) {
        resolvedIds.add(String(serId));
      }
    });

    return {
      serviceIds: [...resolvedIds],
      serviceNames: [...linkedNames],
    };
  };

  const getLinkedServiceIdsForSelectedType = () =>
    getLinkedMatchersForCert(getSelectedCertificate());

  const procedureMatchesSelectedType = (procedure, matchers) => {
    const { serviceIds, serviceNames } = matchers;
    if (!serviceIds?.length && !serviceNames?.length) return false;

    if (
      serviceIds?.length > 0 &&
      serviceIds.includes(String(procedure?.serviceId ?? ""))
    ) {
      return true;
    }

    const procedureName = String(procedure?.procedureName ?? "")
      .trim()
      .toLowerCase();
    if (
      serviceNames?.length > 0 &&
      procedureName &&
      serviceNames.some(
        (name) =>
          name === procedureName ||
          procedureName.includes(name) ||
          name.includes(procedureName),
      )
    ) {
      return true;
    }

    return false;
  };

  const isItemLinkedToRabiesVaccination = (li) => {
    const rabiesCert = getCertificateByLookupName("Rabies Certificate");
    if (!rabiesCert) return false;
    return procedureMatchesSelectedType(
      li,
      getLinkedMatchersForCert(rabiesCert),
    );
  };

  const mapProcedureToPreviewRow = (procedure, detail = null) => ({
    procedureId: procedure.procedureId,
    appointmentId: procedure.appointmentId,
    serviceId: procedure.serviceId,
    procedureName: procedure.procedureName,
    category: procedure.category,
    subCategory: procedure.subCategory,
    doneAt: procedure.doneAt,
    status: procedure.status,
    doctorId: procedure.doctorId,
    doctorNamae: procedure.doctorName,
    createdDate:
      detail?.createdDate ??
      procedure?.doneAt ??
      procedure?.createdDate ??
      null,
    ...(detail ?? {}),
    doseInSeries: procedure?.doseInSeries ?? detail?.doseInSeries,
    serviceRemainderId:
      procedure?.serviceRemainderId ?? detail?.serviceRemainderId,
    certificateNumber:
      procedure?.certificateNumber ?? detail?.certificateNumber,
  });

  const getVaccineData = () => {
    const matchers = getLinkedServiceIdsForSelectedType();
    const hasTypeMatchers =
      matchers.serviceIds.length > 0 || matchers.serviceNames.length > 0;

    const reqList = (certificateProcedure?.data ?? [])?.flatMap((procedure) => {
      if (
        hasTypeMatchers &&
        !procedureMatchesSelectedType(procedure, matchers)
      ) {
        return [];
      }

      const administeredDetails = (procedure?.procedureDetails ?? [])?.filter(
        (detail) => detail.type === "Administered",
      );

      if (administeredDetails?.length > 0) {
        return administeredDetails.map((detail) =>
          mapProcedureToPreviewRow(procedure, detail),
        );
      }

      // Non-vaccine / certificate procedures may have no Administered details
      if (hasTypeMatchers) {
        return [mapProcedureToPreviewRow(procedure)];
      }
      return [];
    });

    const isAllRange = modValues?.range === "all";
    const rangeStart = toDayjsDate(modValues?.startDate);
    const rangeEnd = toDayjsDate(modValues?.endDate);

    if (!isAllRange && (!rangeStart || !rangeEnd)) return [];

    const start =
      !isAllRange && rangeStart.isAfter(rangeEnd) ? rangeEnd : rangeStart;
    const end =
      !isAllRange && rangeStart.isAfter(rangeEnd) ? rangeStart : rangeEnd;

    const filteredList = reqList?.filter((rl) => {
      if (!hasTypeMatchers) {
        // Fallback (e.g. vaccination-only with no linked certificate services)
        if (!rl?.stockId) return false;
        const stckCat = getStockCategoryById(rl?.stockId);
        if (stckCat !== "vaccines") return false;
      }
      if (isAllRange) return true;
      const created = toDayjsDate(rl?.createdDate);
      if (!created) return false;
      return !created.isBefore(start, "day") && !created.isAfter(end, "day");
    });
    return filteredList ?? [];
  };

  const onModalClose = () => {
    setCertModVsble(false);
    setSelectedCert(null);
    setPreviewOpen(false);
    setPreviewCert(null);
    setPrevModVsble(false);
    onCloseMod("close");
  };

  const onCloseCertMod = () => {
    setCertModVsble(false);
    setSelectedCert(null);
  };

  const onClosePreview = () => {
    setPreviewOpen(false);
    setPreviewCert(null);
  };

  const displayOrFallback = (value, fallback = "Not recorded") => {
    if (value == null) return fallback;
    if (typeof value === "string" && value.trim().length === 0) return fallback;
    return value;
  };

  const getDoseNoDisplay = (li) => {
    const doseVal =
      typeof li === "object" && li != null && !Array.isArray(li)
        ? (li?.doseInSeries?.stepId ?? li?.doseInSeries)
        : li;
    if (doseVal == null || doseVal === "") return "";

    if (typeof doseVal === "object") {
      return (
        doseVal?.name ??
        doseVal?.stepName ??
        doseVal?.label ??
        (doseVal?.stepOrder != null ? String(doseVal.stepOrder) : "")
      );
    }

    const remainderId =
      typeof li === "object" && li != null
        ? (li?.doseInSeries?.serviceRemainderId ?? li?.serviceRemainderId)
        : null;
    const serviceId =
      typeof li === "object" && li != null ? li?.serviceId : null;

    for (const tmp of serviceRemainders ?? []) {
      const tmpRemainderId =
        tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;
      if (
        remainderId != null &&
        remainderId !== "" &&
        tmpRemainderId != null &&
        String(tmpRemainderId) !== String(remainderId)
      ) {
        continue;
      }

      if (serviceId != null && serviceId !== "" && remainderId == null) {
        const linked = (tmp?.linkedServices ?? tmp?.serviceIds ?? [])
          ?.map((ser) =>
            typeof ser === "object" && ser !== null
              ? (ser?.serviceId ?? ser?.value)
              : ser,
          )
          ?.filter((id) => id != null && id !== "");
        const linksService = linked?.some(
          (id) => String(id) === String(serviceId),
        );
        if (!linksService) continue;
      }

      const steps = Array.isArray(tmp?.steps)
        ? tmp.steps
        : Array.isArray(tmp?.serviceRemainderSteps)
          ? tmp.serviceRemainderSteps
          : [];
      const matchedStep = steps.find(
        (st) =>
          String(st?.stepId ?? st?.id) === String(doseVal) ||
          String(st?.stepOrder ?? st?.order) === String(doseVal),
      );
      if (matchedStep) {
        const occurrence = tmp?.occurrenceLabel?.trim() || "Dose";
        return (
          matchedStep?.stepName ??
          matchedStep?.name ??
          `${occurrence} ${matchedStep?.stepOrder ?? matchedStep?.order ?? ""}`
        );
      }
    }

    return "";
  };

  const getDateGivenDisplay = (li) => {
    const created = li?.createdDate;
    if (!created) return "";
    return moment(created).format("DD MMM YYYY");
  };

  const getValidUntilDisplay = (li, { subtractOneDay = true } = {}) => {
    const created = li?.createdDate;
    if (!created) return "";

    const doseVal = li?.doseInSeries?.stepId ?? li?.doseInSeries;
    if (doseVal == null || doseVal === "") return "";

    const remainderId =
      li?.doseInSeries?.serviceRemainderId ?? li?.serviceRemainderId;
    const serviceId = li?.serviceId;

    for (const tmp of serviceRemainders ?? []) {
      const tmpRemainderId =
        tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;
      if (
        remainderId != null &&
        remainderId !== "" &&
        tmpRemainderId != null &&
        String(tmpRemainderId) !== String(remainderId)
      ) {
        continue;
      }

      if (serviceId != null && serviceId !== "" && remainderId == null) {
        const linked = (tmp?.linkedServices ?? tmp?.serviceIds ?? [])
          ?.map((ser) =>
            typeof ser === "object" && ser !== null
              ? (ser?.serviceId ?? ser?.value)
              : ser,
          )
          ?.filter((id) => id != null && id !== "");
        const linksService = linked?.some(
          (id) => String(id) === String(serviceId),
        );
        if (!linksService) continue;
      }

      const steps = Array.isArray(tmp?.steps)
        ? tmp.steps
        : Array.isArray(tmp?.serviceRemainderSteps)
          ? tmp.serviceRemainderSteps
          : [];
      if (!steps.length) continue;

      const currentIndex = steps.findIndex(
        (st) =>
          String(st?.stepId ?? st?.id) === String(doseVal) ||
          String(st?.stepOrder ?? st?.order) === String(doseVal),
      );
      if (currentIndex < 0) continue;

      const nextStep =
        steps.slice(currentIndex + 1).find((st) => {
          const days = Number(st?.dueAfterDays ?? st?.due_after_days ?? 0);
          return Number.isFinite(days) && days > 0;
        }) ?? steps[currentIndex + 1];

      if (!nextStep) return "";

      const dueAfterDays = Number(
        nextStep?.dueAfterDays ?? nextStep?.due_after_days ?? 0,
      );
      if (!Number.isFinite(dueAfterDays) || dueAfterDays <= 0) return "";

      const daysToAdd = subtractOneDay ? dueAfterDays - 1 : dueAfterDays;
      return moment(created).add(daysToAdd, "days").format("DD MMM YYYY");
    }

    return "";
  };

  const buildCertificateLivePreview = (cert, li, batch, stock) => {
    const dateGiven = getDateGivenDisplay(li);
    const nextDue = getValidUntilDisplay(li, { subtractOneDay: false });
    const vaccineName = displayOrFallback(
      stock?.productName ?? stock?.name ?? li?.procedureName,
      "",
    );
    const batchNo = displayOrFallback(li?.batchNo ?? batch?.batchNo, "");
    const doseNo = displayOrFallback(getDoseNoDisplay(li), "");
    const route = displayOrFallback(li?.route, "");
    const vetName = displayOrFallback(li?.doctorNamae, "");
    const issuedDate = li?.createdDate
      ? moment(li.createdDate).format("DD MMM YYYY")
      : moment().format("DD MMM YYYY");

    const patient = {
      name: displayOrFallback(petData?.petName),
      owner: displayOrFallback(petData?.userName),
      speciesBreed: displayOrFallback(
        [petData?.petType, petData?.breed].filter(Boolean).join(" — "),
      ),
      ownerAddress: displayOrFallback(petData?.address, "On file at clinic"),
      sexAge: displayOrFallback(
        [
          genderEnum?.[petData?.gender],
          petData?.dob ? getPetAgeDisplay(petData?.dob) : null,
        ]
          .filter(Boolean)
          .join(", "),
      ),
      microchipNo: displayOrFallback(petData?.rfid),
      weightColour: displayOrFallback(
        [petData?.weight ? `${petData.weight} Kg` : null, petData?.color]
          .filter(Boolean)
          .join(" — "),
      ),
      dob:
        petData?.dob?.length > 0
          ? moment(petData.dob).format("DD MMM YYYY")
          : "Not recorded",
    };

    const certId = cert?.id;
    let detailRow;
    if (certId === "std-vaccination") {
      detailRow = [vaccineName, doseNo, batchNo, route, dateGiven, nextDue];
    } else if (certId === "std-rabies") {
      detailRow = [vaccineName, batchNo, dateGiven, nextDue, vetName];
    } else if (certId === "std-fitness") {
      detailRow = [
        li?.procedureName ?? "General Health Checkup",
        dateGiven,
        "",
        "",
        "",
        "",
      ];
    } else if (certId === "std-fit-travel") {
      detailRow = ["", dateGiven, "", nextDue];
    } else if (certId === "std-travel-export") {
      const rabiesName =
        cert?.rabiesSourceService?.name ?? cert?.rabiesVaccination ?? "";
      const parasiteName =
        cert?.parasiteSourceService?.name ?? cert?.parasiteTreatment ?? "";
      detailRow = [
        "",
        "",
        rabiesName ? `${rabiesName}${dateGiven ? `, ${dateGiven}` : ""}` : "",
        parasiteName,
        patient.microchipNo,
      ];
    } else if (certId === "std-sterilization") {
      detailRow = [
        li?.procedureName ?? "Sterilization",
        "",
        dateGiven,
        vetName,
      ];
    } else if (certId === "std-euthanasia") {
      detailRow = [dateGiven, "", "", ""];
    } else if (certId === "std-microchip") {
      detailRow = [patient.microchipNo, dateGiven, "", ""];
    } else {
      detailRow = [li?.procedureName ?? "", dateGiven, vetName, nextDue];
    }

    return {
      patient,
      detailRow,
      certNo: li?.certificateNumber || cert?.name || "Certificate",
      vetName,
      vetLicense: li?.registrationNo ?? "",
      issuedDate,
      note: cert?.note ?? cert?.footerNote,
    };
  };

  const handleViewCertificate = (li, batch) => {
    const stock = getBatch(li?.stockId, null, true);
    let cert;
    if (vaccinationOnly && isItemLinkedToRabiesVaccination(li)) {
      cert = getCertificateByLookupName("Rabies Certificate") ??
        STANDARD_CERTIFICATES.find((c) => c.id === "std-rabies") ?? {
          id: "std-rabies",
          name: "Rabies Certificate",
        };
    } else {
      cert = getSelectedCertificate() ??
        STANDARD_CERTIFICATES.find((c) => c.id === "std-vaccination") ?? {
          id: "std-vaccination",
          name: getCertificateLookupName() || "Certificate",
        };
    }
    setPreviewCert({
      ...cert,
      previewData: buildCertificateLivePreview(cert, li, batch, stock),
    });
    setPreviewOpen(true);
  };

  useEffect(() => {
    if (!modVsble) return;
    if (vaccinationOnly) {
      setModValues(initValues);
      setPrevModVsble(false);
      setCertModVsble(false);
      setSelectedCert(null);
      setPreviewOpen(false);
      setPreviewCert(null);
    }
  }, [modVsble, vaccinationOnly]);

  useEffect(() => {
    if (!prevModVsble || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = contentToPrint.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    const node = contentToPrint.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [prevModVsble, modValues?.type, certificateProcedure]);

  useEffect(() => {
    if (!certModVsble || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = certToPrint.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [certModVsble, selectedCert]);

  const handlePrint = () => {
    printFromNode(contentToPrint.current, {
      title: "",
      pageStyle: a4PrintConfig,
    });
  };

  const handleCertPrint = () => {
    printFromNode(certToPrint.current, {
      title: selectedCert?.certificateNumber
        ? `Certificate ${selectedCert.certificateNumber}`
        : "Certificate of Vaccination",
      pageStyle: a4PrintConfig,
    });
  };

  const getBatch = (stockId, batchId = null, retStock = false) => {
    if (!stockId) return null;
    const reqStock = stocks?.find((stc) => stc?.stockId === stockId);
    if (retStock) return reqStock;
    if (!batchId) return null;
    return (
      reqStock?.stockBatch?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  const renderClinicPrintFooter = () => (
    <div className="prescription-print-footer-inner">
      <div className="mb20 flex-start prescription-print-footer-doctor-row">
        {/* <div>
          <div className="txt-semi-bold fs12 black">Clinician Signature</div>
        </div> */}
      </div>
      {clinicDet?.footer?.length > 0 ? (
        <img src={clinicDet?.footer} alt="" />
      ) : null}
    </div>
  );

  const renderClinicPrintHeader = (title) => (
    <div className="flex-row-ali-cen mv20 cert-print-header-row">
      <div className="w30Per">
        <div>
          {clinicDet?.image ? (
            <img src={clinicDet?.image} className="img-h75" alt="" />
          ) : (
            <div className="flex-center h50img">
              <Typography className="font-bold fs30 white-color capitalize">
                {clinicDet?.name?.[0]}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <div className="w40Per flex-center cert-print-header-title-wrap">
        <div className="font-bold fs20 blue-color cert-print-header-title">
          {title}
        </div>
      </div>
      <div className="w30Per flex1-end">
        <div className="prescription-doc-header-clinic">
          <div className="font-bold blue-color fs16">
            {clinicDet?.name ?? ""}
          </div>
          <div className="txt-regular fs12 blue-color">
            {clinicDet?.address ?? ""}
          </div>
          {clinicDet?.taxId?.length > 0 ? (
            <div className="flex-row-ali-cen mt5">
              <div className="font-bold fs12 blue-color">TAX ID: </div>
              <div className="txt-regular fs12 blue-color ml3">
                {clinicDet?.taxId ?? ""}
              </div>
            </div>
          ) : null}
          {clinicDet?.mobile?.length > 0 ? (
            <div className="flex-row-ali-cen mt5">
              <div className="font-bold fs12 blue-color">Contact: </div>
              <div className="txt-regular fs12 blue-color ml3">
                {clinicDet?.mobile ?? ""}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={onModalClose}
        header={vaccinationOnly ? "Vaccination History" : "Download"}
        rightModal
        modalWidth={30}
      >
        <Grid container spacing={1} className="ph20">
          {!vaccinationOnly ? (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray7 txt-semi-bold mb10">
                Type:{" "}
              </Typography>
              <CustomSelect
                select
                list={downloadTypeList}
                value={modValues?.type}
                handleChange={(e) => onChange("type", e?.target?.value)}
              />
            </Grid>
          ) : null}

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="mt10">
            <Typography className="gray7 txt-semi-bold">Range: </Typography>
            <CustomCheckbox
              radio
              isRow
              name="range"
              value={modValues?.range}
              onChange={(e) => onChange("range", e?.target?.value)}
              radios={[
                { label: "All", value: "all" },
                { label: "Custom", value: "custom" },
              ]}
            />
          </Grid>
          {modValues?.range === "custom" ? (
            <>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <CustomTextField
                  name="startDate"
                  label="Start Date"
                  fullWidth
                  value={modValues?.startDate}
                  handleChange={(e) => onChange("startDate", e?.target?.value)}
                  mobileDate
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <CustomTextField
                  name="endDate"
                  label="End Date"
                  fullWidth
                  value={modValues?.endDate}
                  handleChange={(e) => onChange("endDate", e?.target?.value)}
                  mobileDate
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt20">
              <div className="w30Per">
                <CustomButton text="Preview" smallBtn onClick={handlePreview} />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={prevModVsble}
        onClose={onModalClose}
        header=""
        headerCenter
        modal
        headerBold
        modalWidth={80}
      >
        <div className="scroll-80vh">
          <ContentToPrint
            ref={contentToPrint}
            footer={renderClinicPrintFooter()}
            header={renderClinicPrintHeader(
              getSelectedCertificateType() || "Vaccination history",
            )}
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div className="p5">
                    <div>
                      <div className="flex-row prescription-print-patient-block">
                        <div className="w65Per">
                          <div className="flex-column">
                            <div className="flex-row mv5">
                              {petData?.petType?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color">
                                    Pet Name:
                                  </div>
                                  <div className="font-bold fs12 black capitalize ml5">
                                    {petData?.petName}
                                  </div>
                                </>
                              ) : null}
                              {petData?.petType?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color ml15">
                                    Species:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.petType}
                                  </div>
                                </>
                              ) : null}
                              {petData?.breed?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${petData?.petType?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Breed:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.breed}
                                  </div>
                                </>
                              ) : null}
                            </div>
                            <div className="flex-row mv5">
                              {genderEnum?.[petData?.gender]?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color">
                                    Gender:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {genderEnum?.[petData?.gender]}
                                  </div>
                                </>
                              ) : null}
                              {petData?.weight?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${genderEnum?.[petData?.gender]?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Weight:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.weight} Kg
                                  </div>
                                </>
                              ) : null}
                              {petData?.dob?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${genderEnum?.[petData?.gender]?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Age:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {getPetAgeDisplay(petData?.dob)}
                                  </div>
                                </>
                              ) : null}
                            </div>
                            <div className="flex-row mv5">
                              <div className="font-bold fs12 blue-color">
                                Customer:
                              </div>
                              <div className="font-bold fs12 black capitalize ml5">
                                {petData?.userName}
                              </div>
                            </div>
                            {petData?.address?.length > 0 ? (
                              <div className="flex-row mv5">
                                <div className="w40Per">
                                  <div className="font-bold fs12 blue-color">
                                    Address:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.address}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="w35Per">
                          <div className="flex-column">
                            <div className="flex-row mv5">
                              <div className="font-bold fs12 blue-color">
                                Date:
                              </div>
                              <div className="txt-regular fs12 black ml5">
                                {modValues?.range === "all"
                                  ? "All"
                                  : `${moment(
                                      new Date(modValues?.startDate),
                                    ).format("DD MMM YYYY")} -
                                  ${moment(new Date(modValues?.endDate)).format(
                                    "DD MMM YYYY",
                                  )}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="box-hor-split mv10" />
                      {getVaccineData()?.length > 0 ? (
                        <div
                          className={`prescription-print-table cert-vaccine-print-table${
                            !showFullCertColumns ? " cert-type-print-table" : ""
                          }`}
                        >
                          <div className="flex-row-ali-cen ph10 cert-vaccine-print-col-head">
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Date
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Name
                              </Typography>
                            </div>
                            {showFullCertColumns ? (
                              <>
                                <div className="prescription-print-cell">
                                  <Typography className="fs12 txt-semi-bold black">
                                    Dose No.
                                  </Typography>
                                </div>
                                <div className="prescription-print-cell">
                                  <Typography className="fs12 txt-semi-bold black">
                                    Batch No
                                  </Typography>
                                </div>
                                <div className="prescription-print-cell">
                                  <Typography className="fs12 txt-semi-bold black">
                                    Expiry
                                  </Typography>
                                </div>
                                <div className="prescription-print-cell">
                                  <Typography className="fs12 txt-semi-bold black">
                                    Dose
                                  </Typography>
                                </div>
                                <div className="prescription-print-cell">
                                  <Typography className="fs12 txt-semi-bold black">
                                    Route
                                  </Typography>
                                </div>
                              </>
                            ) : null}
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                {showFullCertColumns
                                  ? "Administered By"
                                  : "Issued By"}
                              </Typography>
                            </div>
                            {showFullCertColumns ? (
                              <div className="prescription-print-cell">
                                <Typography className="fs12 txt-semi-bold black">
                                  Next Due
                                </Typography>
                              </div>
                            ) : null}
                            <div className="prescription-print-cell cert-vaccine-screen-only">
                              <Typography className="fs12 txt-semi-bold black">
                                Certificate
                              </Typography>
                            </div>
                          </div>
                          <div className="blk-hor-split mb3 mt10" />
                          {getVaccineData()?.map((li, i) => {
                            const batch = getBatch(
                              li?.stockId,
                              li?.batchNumber?.value ?? li?.batchId,
                            );
                            return (
                              <div className="mv5" key={`mainLi_${i}`}>
                                <div
                                  className="flex-row ph10 mv5 cert-vaccine-print-row"
                                  key={`rec_${li?.id}`}
                                >
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {moment(li?.createdDate).format(
                                        "DD MMM YYYY",
                                      )}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {li?.procedureName ?? ""}
                                    </Typography>
                                  </div>
                                  {showFullCertColumns ? (
                                    <>
                                      <div className="prescription-print-cell">
                                        <Typography className="fs12 font-medium black4">
                                          {getDoseNoDisplay(li)}
                                        </Typography>
                                      </div>
                                      <div className="prescription-print-cell">
                                        <Typography className="fs12 font-medium black4">
                                          {li?.batchNo ?? batch?.batchNo ?? ""}
                                        </Typography>
                                      </div>
                                      <div className="prescription-print-cell">
                                        <div className="fs12 font-medium black4">
                                          {batch?.expiryDate?.length > 0
                                            ? moment(batch?.expiryDate).format(
                                                "DD MMM YYYY",
                                              )
                                            : ""}
                                        </div>
                                      </div>
                                      <div className="prescription-print-cell">
                                        <Typography className="fs12 font-medium black4">
                                          {li?.dosage ?? ""}
                                          {getRightPackageUom(
                                            batch?.packageUom,
                                            true,
                                          )?.length > 0
                                            ? getRightPackageUom(
                                                batch?.packageUom,
                                                true,
                                              )
                                            : ""}
                                        </Typography>
                                      </div>
                                      <div className="prescription-print-cell">
                                        <Typography className="fs12 font-medium black4">
                                          {li?.route ?? ""}
                                        </Typography>
                                      </div>
                                    </>
                                  ) : null}
                                  <div className="prescription-print-cell">
                                    <div className="fs12 font-medium black4">
                                      {li?.doctorNamae ?? ""}
                                    </div>
                                  </div>
                                  {showFullCertColumns ? (
                                    <div className="prescription-print-cell">
                                      <div className="fs12 font-medium black4">
                                        {getValidUntilDisplay(li, {
                                          subtractOneDay: false,
                                        })}
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="prescription-print-cell cert-vaccine-screen-only">
                                    <Typography
                                      className="fs12 font-medium blue-color underline2 cursor"
                                      onClick={() =>
                                        handleViewCertificate(li, batch)
                                      }
                                    >
                                      View
                                    </Typography>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="no-rec">No records available</div>
                      )}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>
          <div className="flex1-end mv20">
            <div>
              <CustomButton text="Print" onClick={handlePrint} smallBtn />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={certModVsble}
        onClose={onCloseCertMod}
        header=""
        headerCenter
        modal
        headerBold
        modalWidth={80}
      >
        <div className="scroll-80vh">
          <ContentToPrint
            ref={certToPrint}
            footer={renderClinicPrintFooter()}
            header={renderClinicPrintHeader("Certificate of Vaccination")}
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div>
                    <div className="vaccine-cert-no">
                      Certificate No. {selectedCert?.certificateNumber ?? ""}
                    </div>
                    <div className="vaccine-cert-doc">
                      <div className="vaccine-cert-section-title">PATIENT</div>
                      <div className="vaccine-cert-patient-grid">
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Name:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(petData?.petName)}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Owner:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(petData?.userName)}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Species / Breed:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(
                              [petData?.petType, petData?.breed]
                                .filter(Boolean)
                                .join(" — "),
                            )}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Owner Address:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(
                              petData?.address,
                              "On file at clinic",
                            )}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Sex / Age:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(
                              [
                                genderEnum?.[petData?.gender],
                                petData?.dob
                                  ? getPetAgeDisplay(petData?.dob)
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(", "),
                            )}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Microchip No.:
                          </span>
                          <span className="vaccine-cert-field-value">
                            {displayOrFallback(petData?.rfid)}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Weight / Colour:
                          </span>
                          <span className="vaccine-cert-field-value capitalize">
                            {displayOrFallback(
                              [
                                petData?.weight ? `${petData.weight} Kg` : null,
                                petData?.color,
                              ]
                                .filter(Boolean)
                                .join(" — "),
                            )}
                          </span>
                        </div>
                        <div className="vaccine-cert-field">
                          <span className="vaccine-cert-field-label">
                            Date of Birth:
                          </span>
                          <span className="vaccine-cert-field-value">
                            {petData?.dob?.length > 0
                              ? moment(petData.dob).format("DD MMM YYYY")
                              : "Not recorded"}
                          </span>
                        </div>
                      </div>

                      <div className="vaccine-cert-section-title">
                        VACCINATION DETAILS
                      </div>
                      <table className="vaccine-cert-table">
                        <thead>
                          <tr>
                            <th>Vaccine</th>
                            <th>Dose No.</th>
                            <th>Batch No.</th>
                            <th>Route</th>
                            <th>Date Given</th>
                            <th>Valid Until</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>
                              {displayOrFallback(
                                selectedCert?.stock?.productName ??
                                  selectedCert?.stock?.name ??
                                  selectedCert?.procedureName,
                                "",
                              )}
                            </td>
                            <td>
                              {displayOrFallback(
                                getDoseNoDisplay(selectedCert),
                                "",
                              )}
                            </td>
                            <td>
                              {displayOrFallback(
                                selectedCert?.batchNo ??
                                  selectedCert?.batch?.batchNo,
                                "",
                              )}
                            </td>
                            <td>
                              {displayOrFallback(selectedCert?.route, "")}
                            </td>
                            <td>{getDateGivenDisplay(selectedCert)}</td>
                            <td>{getValidUntilDisplay(selectedCert)}</td>
                          </tr>
                        </tbody>
                      </table>

                      <div className="vaccine-cert-statement">
                        This is to certify that the above-named animal was
                        examined and found to be in good health, and was
                        vaccinated as detailed above by the undersigned
                        veterinarian, in accordance with manufacturer guidelines
                        and applicable local regulations.
                      </div>

                      <div className="vaccine-cert-sign-wrap">
                        <div className="vaccine-cert-sign-row">
                          <div className="vaccine-cert-sign-block vaccine-cert-sign-left">
                            <div className="vaccine-cert-sign-line" />
                            <div className="vaccine-cert-sign-name">
                              {displayOrFallback(selectedCert?.doctorNamae, "")}
                            </div>
                            {selectedCert?.registrationNo ? (
                              <div className="vaccine-cert-sign-sub">
                                Veterinary License No.{" "}
                                {selectedCert.registrationNo}
                              </div>
                            ) : (
                              <div className="vaccine-cert-sign-sub">
                                Veterinary License No.
                              </div>
                            )}
                          </div>
                          <div className="vaccine-cert-sign-block vaccine-cert-sign-right">
                            <div className="vaccine-cert-sign-line" />
                            <div className="vaccine-cert-sign-name">
                              {selectedCert?.createdDate
                                ? moment(selectedCert.createdDate).format(
                                    "DD MMM YYYY",
                                  )
                                : moment().format("DD MMM YYYY")}
                            </div>
                            <div className="vaccine-cert-sign-sub">
                              Date Issued
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>
          <div className="flex-center mv20">
            <div>
              <CustomButton
                text="Print / Download PDF"
                onClick={handleCertPrint}
                smallBtn
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CertificatePreviewModal
        open={previewOpen}
        cert={previewCert}
        onClose={onClosePreview}
      />
    </>
  );
};

export default CerificatesDownloadModal;
